import React, { useCallback, useState } from 'react'
import { useMutation } from 'react-query'
import {
    StyledBodyTextContainer,
    StyledColumnContainer,
    StyledContainer,
    StyledCTAColumn,
    StyledCTAWardenButton,
    StyledPuzzlesCTAContainer,
    StyledRowContainer,
    StyledTextContainer,
    StyledTextLink,
    StyledTNHeaderLogo,
} from './TNCreateAccountCTA.styled'
import {
    AppState,
    AuthenticationState,
    getTheNightlyEmailVerifyActionPath,
} from '@news-mono/web-common'
import { useSelector } from 'react-redux'
import { useAuthActions } from '../../authentication'
import StyledWardenButton from '../components/StyledWardenButton'
import { tokens } from '@news-mono/design-tokens'
import { useSendVerifyEmail } from '../../../user-setting/hooks/useSendVerifyEmail'
import { TNToastComponent } from '../../../content/TNToastNotification/TNToastNotification'
import { TNToastComponentProps } from '../../../content/TNToastNotification/TNToastNotification.routing'

export const TNCreateAccountCTA: React.FC = () => {
    const { isLoggedIn } = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )
    const { onRegisterClick } = useAuthActions()

    return (
        <StyledContainer showAsDivider={isLoggedIn}>
            {!isLoggedIn && (
                <StyledRowContainer>
                    <StyledColumnContainer>
                        <StyledTNHeaderLogo fill="#000" />
                        <StyledTextContainer>
                            Join The Nightly for full access.
                        </StyledTextContainer>
                    </StyledColumnContainer>
                    <StyledColumnContainer>
                        <StyledWardenButton
                            onClick={onRegisterClick}
                            backgroundColors={{
                                default:
                                    tokens.thenightly.colors.palette.utility
                                        .error.default,
                                hover: tokens.thenightly.colors.palette.utility
                                    .error.hover,
                            }}
                            roundEdges={true}
                            useWidePadding={true}
                        >
                            CREATE FREE ACCOUNT
                        </StyledWardenButton>
                    </StyledColumnContainer>
                </StyledRowContainer>
            )}
        </StyledContainer>
    )
}

export const TNPuzzlesCreateAccountCTA: React.FC = () => {
    const {
        isLoggedIn,
        isLoading,
        emailVerified,
        wanUserId: auth0Id,
    } = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )
    const { onRegisterClick, onLoginClick } = useAuthActions()

    const [emailSendAttempted, setEmailSendAttempted] = useState<Boolean>(false)
    const [emailStatusbanner, setEmailStatusBanner] =
        useState<TNToastComponentProps>({
            type: 'success',
            text: 'Email has been sent',
            useMaxWidth: false,
            forceMaxWidth: true,
        })

    const onClickEmailVerify = async () => {
        const request = 'send-verification-email'

        const response = await fetch(getTheNightlyEmailVerifyActionPath, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: auth0Id }),
        })
        if (!response.ok || response.status !== 201) {
            setEmailSendAttempted(true)
            setEmailStatusBanner({
                type: 'error',
                text: 'Something went wrong.',
                useMaxWidth: false,
                forceMaxWidth: true,
            })
            throw new Error(`Network error in ${request}`)
        }

        setEmailSendAttempted(true)
        setEmailStatusBanner({
            type: 'success',
            text: 'Email has been sent',
            useMaxWidth: false,
            forceMaxWidth: true,
        })
        return true
    }

    if (isLoggedIn && !emailVerified && !isLoading) {
        return (
            <StyledPuzzlesCTAContainer showAsDivider={false}>
                <StyledCTAColumn>
                    {emailSendAttempted && (
                        <TNToastComponent
                            type={emailStatusbanner?.type}
                            text={emailStatusbanner?.text}
                            useMaxWidth={emailStatusbanner?.useMaxWidth}
                            forceMaxWidth={emailStatusbanner?.forceMaxWidth}
                        />
                    )}
                    <StyledTextContainer>
                        Verify your email to access Puzzles.
                    </StyledTextContainer>

                    <StyledBodyTextContainer>
                        Your email address hasn't been verified. Verify your
                        email to access Puzzles.
                    </StyledBodyTextContainer>

                    <StyledCTAWardenButton
                        onClick={onClickEmailVerify}
                        disabled={emailSendAttempted ? true : false}
                        backgroundColors={{
                            default:
                                tokens.thenightly.colors.palette.utility.error
                                    .default,
                            hover: tokens.thenightly.colors.palette.utility
                                .error.hover,
                        }}
                        roundEdges={true}
                        useWidePadding={true}
                    >
                        {emailSendAttempted
                            ? 'RESEND VERIFICATION EMAIL'
                            : 'SEND VERIFICATION EMAIL'}
                    </StyledCTAWardenButton>
                </StyledCTAColumn>
            </StyledPuzzlesCTAContainer>
        )
    }

    if (!isLoggedIn && !isLoading) {
        return (
            <StyledPuzzlesCTAContainer showAsDivider={false}>
                <StyledCTAColumn>
                    <StyledTextContainer>
                        Join The Nightly to access Puzzles.
                    </StyledTextContainer>

                    <StyledBodyTextContainer>
                        Get The Nightly, weekly to your inbox. <br></br> Add
                        your comment. Never miss a story.
                    </StyledBodyTextContainer>

                    <StyledCTAWardenButton
                        onClick={onRegisterClick}
                        backgroundColors={{
                            default:
                                tokens.thenightly.colors.palette.utility.error
                                    .default,
                            hover: tokens.thenightly.colors.palette.utility
                                .error.hover,
                        }}
                        roundEdges={true}
                        useWidePadding={true}
                    >
                        CREATE FREE ACCOUNT
                    </StyledCTAWardenButton>

                    <StyledBodyTextContainer>
                        Already have an account?
                        <StyledTextLink onClick={onLoginClick}>
                            Log in here
                        </StyledTextLink>
                    </StyledBodyTextContainer>
                </StyledCTAColumn>
            </StyledPuzzlesCTAContainer>
        )
    }

    return <></>
}
